import {
  AddIcon,
  Alert,
  Button,
  CheckIcon,
  CloseIcon,
  DeleteIcon,
  Dialog,
  EditIcon,
  FormStack,
  IconButton,
  Stack,
  Tag,
  TextField,
  Typography,
} from '@all-manager/design-system-v2';
import { useManageProviderTagsModal } from './useManageProviderTagsModal';

type Props = {
  onClose: () => void;
};

const itemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  borderRadius: 8,
  backgroundColor: 'rgba(0, 0, 0, 0.04)',
};

const editContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flex: 1,
  marginRight: 8,
};

export default function ManageProviderTagsModal(props: Props) {
  const hook = useManageProviderTagsModal();

  return (
    <Dialog
      open
      onClose={props.onClose}
      title="Gérer les tags des prestataires"
      closeLabel="Fermer"
      maxWidth="sm"
      fullWidth
      actions={{
        primaryAction: {
          label: 'Fermer',
          onClick: props.onClose,
        },
      }}
    >
      <FormStack>
        {hook.errorMessage ? <Alert severity="error">{hook.errorMessage}</Alert> : null}

        <Typography variant="body2">
          Créez et organisez les spécialités et métiers pour faciliter la recherche de vos prestataires.
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            value={hook.newTagName}
            onChange={(e) => hook.setNewTagName(e.target.value)}
            placeholder="Nouveau tag (ex. Plomberie, Ascenseur…)"
            size="small"
            fullWidth
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                hook.handleCreateTag();
              }
            }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={hook.handleCreateTag}
            disabled={!hook.newTagName.trim() || hook.isSubmitting}
          >
            Ajouter
          </Button>
        </Stack>

        <Stack spacing={1}>
          {hook.tags.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px 0', opacity: 0.7 }}>
              <Typography variant="body2">
                Aucun tag enregistré pour le moment.
              </Typography>
            </div>
          ) : (
            hook.tags.map((tag) => (
              <div key={tag.id} style={itemStyle}>
                {hook.editingTagId === tag.id ? (
                  <div style={editContainerStyle}>
                    <TextField
                      value={hook.editingTagName}
                      onChange={(e) => hook.setEditingTagName(e.target.value)}
                      size="small"
                      fullWidth
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          hook.handleSaveEdit(tag.id);
                        } else if (e.key === 'Escape') {
                          hook.handleCancelEdit();
                        }
                      }}
                    />
                    <IconButton
                      tooltip="Enregistrer"
                      size="small"
                      color="primary"
                      onClick={() => hook.handleSaveEdit(tag.id)}
                      disabled={!hook.editingTagName.trim() || hook.isSubmitting}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      tooltip="Annuler"
                      size="small"
                      onClick={hook.handleCancelEdit}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
                ) : (
                  <>
                    <Tag label={tag.name} color="default" variantIcon='label' />
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        tooltip="Modifier le tag"
                        size="small"
                        onClick={() => hook.handleStartEdit(tag)}
                        disabled={hook.isSubmitting}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        tooltip="Supprimer le tag"
                        size="small"
                        color="error"
                        onClick={() => hook.handleDeleteTag(tag.id)}
                        disabled={hook.isSubmitting}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </>
                )}
              </div>
            ))
          )}
        </Stack>
      </FormStack>
    </Dialog>
  );
}
